import { useMemo, useState } from "react";
import { hace, instante } from "../../shared/ficha";
import { diaAr } from "../../shared/fechaAr";

/* El bloque de multas de tránsito de la ficha (PBA/CABA/SINAI, vía
 * fibot-vehicle-intelligence).
 *
 * ── ES UN GASTO A PROPÓSITO, Y POR ESO NO SE CONSULTA SOLO ──────────────
 *
 * Cada consulta resuelve un reCAPTCHA real: cuesta plata y tarda (hasta ~3
 * minutos). Por eso este bloque no dispara nada al abrir la ficha -- muestra
 * lo que ya se sabe (puede ser de hace semanas) y deja el botón para pedir
 * una consulta nueva a quien decida que vale la pena.
 *
 * ── `pagada` LA DECIDE UNA PERSONA, EL ORGANISMO NO ──────────────────────
 *
 * Tildar descuenta del total impago. `discrepancia` (una multa marcada
 * pagada que la re-consulta sigue trayendo) se muestra siempre, nunca se
 * esconde: puede ser que el pago no impactó todavía, o que se tildó la
 * patente equivocada -- las dos se resuelven mirando la fila.
 */

/* Exportado: el aviso de multas impagas de arriba de la ficha dice el mismo
 * total, y con dos formateadores el día que uno cambie el redondeo el aviso y
 * el bloque dirían montos distintos de la misma deuda. */
export const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return "$" + new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(v);
};

const ESTADO_LEGIBLE = {
  ok: null, // no hace falta decir "PBA: ok"
  captcha_required: "no se pudo resolver el captcha",
  provider_error: "el portal no respondió",
  parse_error: "no se pudo leer la respuesta",
  unsupported_format: "trae resultado pero el parser todavía no lo cubre",
  unauthorized: "el portal rechazó el token",
};

const BloqueMultas = ({
  dominio,
  filas,
  consulta,
  puedeConsultar,
  onConsultar,
  onMarcarPagada,
  onMarcarTodasPagadas,
}) => {
  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState(null);
  const [ocupada, setOcupada] = useState(null); // id de la fila en vuelo

  const actas = useMemo(() => filas || [], [filas]);
  const impagas = useMemo(() => actas.filter((a) => !a.pagada), [actas]);
  const totalImpago = useMemo(
    () => impagas.reduce((acc, a) => acc + (Number(a.monto) || 0), 0),
    [impagas],
  );
  const discrepancias = useMemo(() => actas.filter((a) => a.discrepancia), [actas]);

  const avisos = (consulta?.avisos || []).filter(Boolean);
  const estadosAvisos = Object.entries(consulta?.estados || {})
    .map(([jur, est]) => (ESTADO_LEGIBLE[est] ? `${jur}: ${ESTADO_LEGIBLE[est]}` : null))
    .filter(Boolean);

  const consultar = async () => {
    setConsultando(true);
    setError(null);
    try {
      await onConsultar();
    } catch (e) {
      setError(e?.message || "No se pudo consultar");
    } finally {
      setConsultando(false);
    }
  };

  const marcar = async (id, pagada) => {
    setOcupada(id);
    setError(null);
    try {
      await onMarcarPagada(id, pagada);
    } catch (e) {
      setError(e?.message || "No se pudo guardar");
    } finally {
      setOcupada(null);
    }
  };

  const marcarTodas = async () => {
    setOcupada("__todas__");
    setError(null);
    try {
      await onMarcarTodasPagadas();
    } catch (e) {
      setError(e?.message || "No se pudo guardar");
    } finally {
      setOcupada(null);
    }
  };

  return (
    <div>
      <div className="pf-multas-cab">
        <div>
          {consulta?.consultada_en ? (
            <p className="tenue">
              Consultado {hace(consulta.consultada_en)} ({instante(consulta.consultada_en)})
            </p>
          ) : (
            <p className="tenue">Nunca se consultó este dominio.</p>
          )}
          {(estadosAvisos.length > 0 || avisos.length > 0) && (
            <p className="pf-nopude">
              {[...estadosAvisos, ...avisos].join(" · ")}
            </p>
          )}
        </div>
        {puedeConsultar && dominio && (
          <button
            type="button"
            className="pf-mover__btn"
            disabled={consultando}
            onClick={consultar}
            title="Consulta real contra PBA/CABA/SINAI. Puede tardar hasta 3 minutos."
          >
            {consultando ? "Consultando… (hasta 3 min)" : "Consultar ahora"}
          </button>
        )}
      </div>

      {error && <p className="pf-nopude">{error}</p>}

      {actas.length === 0 ? (
        <p className="tenue">
          {consulta?.consultada_en
            ? "No hay multas registradas a la fecha."
            : "Sin datos todavía — pedí una consulta."}
        </p>
      ) : (
        <>
          <p className="pf-dato">
            <b>{impagas.length}</b> impaga(s) de {actas.length} · {money(totalImpago)}
            {discrepancias.length > 0 && (
              <span className="pill mal"> {discrepancias.length} con discrepancia</span>
            )}
          </p>

          {puedeConsultar && impagas.length > 0 && (
            <button
              type="button"
              className="pf-mover__btn"
              disabled={ocupada === "__todas__"}
              onClick={marcarTodas}
            >
              Marcar todas pagadas
            </button>
          )}

          <div className="tabla-wrap">
            <table className="tabla fija">
              <thead>
                <tr>
                  {puedeConsultar && <th className="col-tilde">Pagada</th>}
                  <th>Jurisdicción</th>
                  <th>Acta</th>
                  <th>Fecha</th>
                  <th>Descripción</th>
                  <th>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {actas.map((a) => (
                  <tr key={a.id} className={a.discrepancia ? "mal" : a.pagada ? "tenue" : ""}>
                    {puedeConsultar && (
                      <td className="col-tilde">
                        <input
                          type="checkbox"
                          checked={a.pagada}
                          disabled={ocupada === a.id}
                          onChange={(e) => marcar(a.id, e.target.checked)}
                          aria-label={`Marcar acta ${a.acta} como pagada`}
                        />
                      </td>
                    )}
                    <td>{a.jurisdiccion}</td>
                    <td>{a.acta}</td>
                    <td>{diaAr(a.ocurrida_en) || "—"}</td>
                    <td>{a.descripcion || "—"}</td>
                    <td>{money(a.monto)}</td>
                    <td>
                      {a.pagada ? (
                        <span className="pill">
                          Pagada{a.pagada_por_nombre ? ` · ${a.pagada_por_nombre}` : ""}
                        </span>
                      ) : (
                        a.estado_fuente || "—"
                      )}
                      {a.discrepancia && (
                        <div className="pf-nopude">
                          Marcada pagada pero el organismo la sigue listando
                          {a.discrepancia_detectada_en
                            ? ` (visto ${hace(a.discrepancia_detectada_en)})`
                            : ""}
                          .
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BloqueMultas;
